import { useEffect, useRef, useCallback } from 'react';
import * as PIXI from 'pixi.js';
import { Match3Board, GameConfig } from '../engine/Match3Board';

interface GameCanvasProps {
  level: any;
  onGameStart: () => void;
  onScoreUpdate: (score: number) => void;
  onMovesUpdate: (moves: number) => void;
  onGoalsUpdate: (goals: any[]) => void;
  onGameOver: (completed: boolean, score: number) => void;
  isPaused: boolean;
  gameEngineRef: any;
}

const GameCanvas = ({
  level, onGameStart, onScoreUpdate, onMovesUpdate, onGoalsUpdate, onGameOver, isPaused, gameEngineRef
}: GameCanvasProps) => {
  const canvasRef = useRef<HTMLDivElement>(null);
  const appRef = useRef<PIXI.Application | null>(null);
  const boardRef = useRef<Match3Board | null>(null);

  const initializeGame = useCallback(async () => {
    if (!canvasRef.current || appRef.current) return;

    try {
      // Use full window size
      const app = new PIXI.Application();
      await app.init({
        width: window.innerWidth,
        height: window.innerHeight,
        backgroundColor: 0x060612,
        antialias: true,
        resolution: window.devicePixelRatio || 1,
        autoDensity: true,
      });

      appRef.current = app;
      canvasRef.current.innerHTML = '';
      canvasRef.current.appendChild(app.canvas);

      const config: GameConfig = {
        width: level.gridSize.width,
        height: level.gridSize.height,
        gemTypes: level.gemTypes,
        moveLimit: level.moveLimit || 0,
        obstacles: level.obstacles || [],
        goals: level.goals || [],
      };

      const board = new Match3Board(app, config);
      boardRef.current = board;
      gameEngineRef.current = board;

      board.onScore = (s) => onScoreUpdate(s);
      board.onMoves = (m) => onMovesUpdate(m);
      board.onGoals = (g) => onGoalsUpdate(g);
      board.onEnd = (completed, score) => onGameOver(completed, score);

      await board.init();
      onGameStart();
    } catch (e: any) {
      console.error('[GameCanvas] Error:', e);
      if (canvasRef.current) {
        canvasRef.current.innerHTML = `<div style="color:#ff4757;padding:40px;text-align:center;font-size:18px">
          <h3>Game Error</h3><p>${e.message}</p>
          <pre style="text-align:left;font-size:12px;white-space:pre-wrap">${e.stack}</pre>
        </div>`;
      }
    }
  }, [level, onGameStart, onScoreUpdate, onMovesUpdate, onGoalsUpdate, onGameOver, gameEngineRef]);

  useEffect(() => {
    initializeGame();
    return () => {
      if (appRef.current) { appRef.current.destroy(true); appRef.current = null; }
      boardRef.current = null;
    };
  }, [initializeGame]);

  useEffect(() => {
    if (boardRef.current) { isPaused ? boardRef.current.pause() : boardRef.current.resume(); }
  }, [isPaused]);

  useEffect(() => {
    const handler = () => { if (boardRef.current) boardRef.current.resize(); };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  return <div ref={canvasRef} className="game-canvas" style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0 }} />;
};

export default GameCanvas;
